/**
 * BeBobi car-model size fitter (product page).
 * Types a car model → picks a size → selects the product's "Rozmiar" variant
 * option by checking the matching radio and dispatching `change`, which
 * Horizon's <variant-picker> listens for and uses to update the variant.
 */
import { matchCar, SIZE_HINT } from '@theme/bebobi-car-map';
import { ThemeEvents } from '@theme/events';
const esc = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

class BebobiCarFitter extends HTMLElement {
  connectedCallback() {
    this.optionName = (this.dataset.optionName || 'Rozmiar');
    this.sizeButtons = [...this.querySelectorAll('[data-fitter-sizes] .bb-cf__size')];
    this.carInput = this.querySelector('[data-fitter-car]');
    this.note = this.querySelector('[data-fitter-note]');
    this.noteText = this.querySelector('[data-fitter-note-text]');
    this.available = this.sizeButtons.map((b) => b.dataset.key);

    this.sizeButtons.forEach((b) => b.addEventListener('click', () => this.pick(b.dataset.value, b.dataset.key, false)));
    this.carInput?.addEventListener('input', () => this.onCar());

    /* The fitter is the only visible size control (the picker's Rozmiar row is
       hidden when the fitter renders), so it must mirror the actual selection:
       on load (also deep links with ?variant=) and whenever the picker's
       radios change from anywhere else. */
    this.syncFromPicker();
    /* BeBobi 14.10.2026: resync after ANY change inside the variant picker, not only a
       Rozmiar change. bebobi-variant-autosize.js switches the size on a colour change
       with updateSelectedOption(), which fires no change event on the size radio, so the
       tiles kept showing the old size while the form held another variant. This listener
       runs in the bubble phase, after the picker has already updated its radios. */
    this._onDocChange = (e) => {
      const t = e.target;
      if (t instanceof Element && t.closest('variant-picker')) this.syncFromPicker();
    };
    document.addEventListener('change', this._onDocChange);
    /* ...and after Horizon re-renders the picker for the new variant (VariantUpdateEvent
       bubbles from <variant-picker>): the radios' data-option-available is fresh then. */
    this._scope = this.closest('dialog, .shopify-section');
    this._onVariantUpdate = () => this.syncFromPicker();
    this._scope?.addEventListener(ThemeEvents.variantUpdate, this._onVariantUpdate);
  }

  disconnectedCallback() {
    document.removeEventListener('change', this._onDocChange);
    this._scope?.removeEventListener(ThemeEvents.variantUpdate, this._onVariantUpdate);
    this._scope = null;
  }

  /** Rozmiar radios of the picker next to this fitter (same section or quick-add dialog). */
  sizeRadios() {
    const name = this.optionName.toLowerCase();
    const match = (inp) => {
      const n = (inp.name || '').toLowerCase();
      return n === name || n.startsWith(name + '-');
    };
    const scope = this.closest('dialog, .shopify-section');
    let radios = scope ? [...scope.querySelectorAll('variant-picker input[type="radio"]')].filter(match) : [];
    if (!radios.length) radios = [...document.querySelectorAll('input[type="radio"]')].filter(match);
    return radios;
  }

  /** Mirror the currently checked Rozmiar radio (or select) onto the tiles. */
  syncFromPicker() {
    const name = this.optionName.toLowerCase();
    const radios = this.sizeRadios();
    let value = null;
    for (const inp of radios) {
      if (inp.checked) { value = inp.value; break; }
    }
    /* Sold-out sizes: data-option-available comes from snippets/bebobi-option-available
       (orderable with the SELECTED colour). Tile stays clickable; picking it shows Wyprzedane. */
    const soldOut = this.dataset.soldOutLabel || '';
    this.sizeButtons.forEach((b) => {
      const r = radios.find((inp) => inp.value === b.dataset.value);
      const off = !!r && r.dataset.optionAvailable === 'false';
      b.classList.toggle('is-unavailable', off);
      if (off && soldOut) b.setAttribute('aria-label', `${b.dataset.value} - ${soldOut}`);
      else b.removeAttribute('aria-label');
    });
    if (value === null) {
      for (const sel of document.querySelectorAll('select')) {
        if ((sel.name || '').toLowerCase().includes(name)) { value = sel.value; break; }
      }
    }
    if (value === null) return;
    this.sizeButtons.forEach((b) => b.classList.toggle('is-selected', b.dataset.value === value));
  }

  onCar() {
    const v = this.carInput.value.trim();
    if (v.length < 2) { this.hideNote(); return; }
    const found = matchCar(v);
    let key = found.size;
    // this product may not offer every size (e.g. no XS) — map to the nearest available
    if (!this.available.includes(key)) {
      if (key === 'XS' && this.available.includes('S')) key = 'S';
      else key = this.available.includes('M') ? 'M' : this.available[0];
    }
    const btn = this.sizeButtons.find((b) => b.dataset.key === key);
    this.pick(btn ? btn.dataset.value : key, key, true);
    if (found.matched) this.showNote(`Dla <b>${esc(v)}</b> polecamy rozmiar <b>${key}</b> — ustawiliśmy go za Ciebie.`);
    else this.showNote('Nie rozpoznaliśmy modelu — najczęściej pasuje <b>M</b>. Napisz do nas, dobierzemy pewnie.');
  }

  pick(value, key, fromCar) {
    this.sizeButtons.forEach((b) => b.classList.toggle('is-selected', b.dataset.value === value));
    this.selectOption(value);
    if (!fromCar) {
      const hint = SIZE_HINT[key];
      if (hint) this.showNote(`Rozmiar <b>${esc(key)}</b> pasuje do: ${esc(hint)}.`);
    }
  }

  /** Select the product's "Rozmiar" option value in Horizon's variant picker. */
  selectOption(value) {
    const name = this.optionName.toLowerCase();
    // buttons style → radio inputs named "Rozmiar-<blockId>-<productId>"
    const radios = document.querySelectorAll('input[type="radio"]');
    for (const inp of radios) {
      const n = (inp.name || '').toLowerCase();
      if ((n === name || n.startsWith(name + '-')) && inp.value === value) {
        if (!inp.checked) { inp.checked = true; inp.dispatchEvent(new Event('change', { bubbles: true })); }
        return true;
      }
    }
    // dropdown style → <select name="options[Rozmiar]">
    const selects = document.querySelectorAll('select');
    for (const sel of selects) {
      if ((sel.name || '').toLowerCase().includes(name)) {
        if (sel.value !== value) { sel.value = value; sel.dispatchEvent(new Event('change', { bubbles: true })); }
        return true;
      }
    }
    return false;
  }

  showNote(html) {
    if (!this.note || !this.noteText) return;
    this.noteText.innerHTML = html;
    this.note.classList.add('is-shown');
  }

  hideNote() {
    this.note?.classList.remove('is-shown');
  }
}

if (!customElements.get('bebobi-car-fitter')) {
  customElements.define('bebobi-car-fitter', BebobiCarFitter);
}
